import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useContext,
  createContext,
} from 'react';
import { getData } from '../Services/Util';
import { populateMenu } from '../Services/AppAction';

// ─────────────────────────────────────────────
// Enum
// ─────────────────────────────────────────────

export enum MenuID {
  // Core navigation
  HOME = 'home',
  SCHEMES = 'schemes',
  ORDERS = 'orders',
  PRODUCTS = 'products',
  TRANSACTIONS = 'transactions',
  PROFILE = 'profile',
  CART = 'cart',
  SEARCH = 'search',

  // Auth / onboarding
  REGISTRATION = 'registration',
  SETMPIN = 'setmpin',
  OTP = 'otp',
  LOGINMPIN = 'loginmpin',
  SELECTDEALER = 'selectdealer',
  CHANGEDEALER = 'changedealer',

  // Home sub-sections
  SAVINGS = 'savings',
  BENEFITS = 'benefits',
  FOC = 'foc',
  FREQUENTLY_BOUGHT = 'frequentlybought',
  QUICK_REORDER = 'quickreorder',
  HOT_SCHEME_PRODUCTS = 'hotschemeproducts',
  TRENDING = 'trending',
  LOCAL_FAVORITES = 'localfavorites',
  RECOMMENDED = 'recommendedproducts',
  PROMO_BANNER = 'promotionalbanner',

  // Scheme sub-types
  SCHEMES_VALUE = 'schemesvalue',
  SCHEMES_FOC = 'schemesfoc',

  // Profile sub-sections
  KYC = 'kyc',
  ADDRESS_BOOK = 'addressbook',
  FAVORITES = 'favorites',
  ORDER_ACCESS = 'orderaccess',
  LOGOUT = 'logout',
  ALL_TRANSACTIONS = 'alltransactions'
}

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export interface MenuState {
  [key: string]: boolean | null;
}

export interface MenuAccessConfig {
  menuId: string;
  isEnabled: boolean;
}

interface MenuLayoutContextType {
  menuStates: MenuState;
  menuReady: boolean; // true once API fetch is done (or skipped)
  setMenuState: (menuId: string, state: boolean) => void;
  setMultipleMenuStates: (states: Partial<MenuState>) => void;
  setMenuStatesFromConfig: (config: MenuAccessConfig[]) => void;
  applyMenuFromApiResponse: (table: { MENU_CODE: string; STATUS: string }[]) => void;
  initializeMenu: () => Promise<void>; // call after login / dealer switch
  isMenuAccessible: (menuId: string) => boolean;
  getActiveMenus: () => string[];
  resetMenuStates: () => void;
}

// ─────────────────────────────────────────────
// Initial state — all null (not yet determined)
// ─────────────────────────────────────────────

const initialMenuStates: MenuState = Object.values(MenuID).reduce<MenuState>(
  (acc, id) => ({ ...acc, [id]: null }),
  {},
);

// ─────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────

export const MenuLayoutContext = createContext<MenuLayoutContextType | undefined>(undefined);

// ─────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────

interface MenuLayoutProviderProps {
  children: React.ReactNode;
}

export const MenuLayoutProvider: React.FC<MenuLayoutProviderProps> = ({ children }) => {
  const [menuStates, setMenuStates] = useState<MenuState>(initialMenuStates);
  const [menuReady, setMenuReady] = useState(false);

  // ── Setters ────────────────────────────────────────────────────────────

  const setMenuState = useCallback((menuId: string, state: boolean) => {
    setMenuStates(prev => ({ ...prev, [menuId]: state }));
  }, []);

  const setMultipleMenuStates = useCallback((states: Partial<MenuState>) => {
    setMenuStates(prev => {
      const next = { ...prev };
      Object.entries(states).forEach(([k, v]) => {
        if (v !== undefined) {next[k] = v;}
      });
      return next;
    });
  }, []);

  const setMenuStatesFromConfig = useCallback((config: MenuAccessConfig[]) => {
    setMenuStates(prev => {
      const next = { ...prev };
      config.forEach(({ menuId, isEnabled }) => {
        next[menuId] = isEnabled;
      });
      return next;
    });
  }, []);

  const applyMenuFromApiResponse = useCallback(
    (table: { MENU_CODE: string; STATUS: string }[]) => {
      const config: MenuAccessConfig[] = table.map(item => ({
        menuId: item.MENU_CODE,
        isEnabled: item.STATUS === 'Y',
      }));
      setMenuStatesFromConfig(config);
    },
    [setMenuStatesFromConfig],
  );

  // ── Core fetch logic (reusable: boot + post-login + dealer switch) ─────

  const initializeMenu = useCallback(async () => {
    try {
      const userData = await getData('userData');

      // No user data yet (not logged in) — skip silently
      if (!userData?.PARTY_ID || !userData?.mobileNumber) {return;}

      const menuRes = await populateMenu(userData.PARTY_ID, userData.mobileNumber);

      if (menuRes?.status === 0 && Array.isArray(menuRes?.data)) {
        applyMenuFromApiResponse(menuRes.data);
      } else {
        console.warn('populateMenu: unexpected response', menuRes);
      }
    } catch (err) {
      console.error('initializeMenu failed:', err);
    } finally {
      setMenuReady(true);
    }
  }, [applyMenuFromApiResponse]);

  // ── Auto-fetch on mount if user is already logged in ──────────────────

  useEffect(() => {
    initializeMenu();
  }, [initializeMenu]);

  // ── Re-fetch when user logs in (detect userData changes) ──────────────
  useEffect(() => {
    const watchUserData = async () => {
      const userData = await getData('userData');
      // Only trigger if user just logged in (has PARTY_ID)
      if (userData?.PARTY_ID && !menuReady) {
        await initializeMenu();
      }
    };

    const interval = setInterval(watchUserData, 500);
    return () => clearInterval(interval);
  }, [menuReady, initializeMenu]);

  // ── Queries ────────────────────────────────────────────────────────────

  const isMenuAccessible = useCallback(
    (menuId: string): boolean => menuStates[menuId] === true,
    [menuStates],
  );

  const getActiveMenus = useCallback(
    (): string[] =>
      Object.entries(menuStates)
        .filter(([, state]) => state === true)
        .map(([id]) => id),
    [menuStates],
  );

  const resetMenuStates = useCallback(() => {
    setMenuStates(initialMenuStates);
    setMenuReady(false);
  }, []);

  // ── Context value ──────────────────────────────────────────────────────

  const value = useMemo(
    () => ({
      menuStates,
      menuReady,
      setMenuState,
      setMultipleMenuStates,
      setMenuStatesFromConfig,
      applyMenuFromApiResponse,
      initializeMenu,
      isMenuAccessible,
      getActiveMenus,
      resetMenuStates,
    }),
    [
      menuStates,
      menuReady,
      setMenuState,
      setMultipleMenuStates,
      setMenuStatesFromConfig,
      applyMenuFromApiResponse,
      initializeMenu,
      isMenuAccessible,
      getActiveMenus,
      resetMenuStates,
    ],
  );

  return (
    <MenuLayoutContext.Provider value={value}>
      {children}
    </MenuLayoutContext.Provider>
  );
};

// ─────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────

export const useMenuLayout = (): MenuLayoutContextType => {
  const context = useContext(MenuLayoutContext);
  if (!context) {throw new Error('useMenuLayout must be used within MenuLayoutProvider');}
  return context;
};

export default MenuLayoutProvider;
